import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  seoConfig,
  formatTitle,
  SITE_URL,
  SITE_NAME,
  pickLocalized,
  ogImageForRoute,
} from "@/lib/seo-config";
import { stripLangPrefix, withLangPrefix, SUPPORTED_LANGS, type SupportedLang, getLangFromPath } from "@/lib/i18n-routing";
import { buildPageJsonLd } from "@/lib/seo-jsonld";

type Props = {
  /** Override route auto-detection (e.g. for dynamic routes). */
  path?: string;
  /** Override title for this render. */
  title?: string;
  /** Override description for this render. */
  description?: string;
  /** Override OG image. */
  image?: string;
  /** Force noindex regardless of config. */
  noIndex?: boolean;
};

const SEO = ({ path, title, description, image, noIndex }: Props) => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const fullPath = path ?? pathname;
  // Canonical (English-style) route key for seoConfig lookup.
  const canonicalRoute = stripLangPrefix(fullPath);
  const entry = seoConfig[canonicalRoute] ?? seoConfig["/"];

  const urlLang = getLangFromPath(fullPath);
  const lang = (i18n.language as SupportedLang) || urlLang || "en";

  const rawTitle = title ?? pickLocalized(entry.title, lang);
  const finalTitle = formatTitle(rawTitle);
  const finalDescription = description ?? pickLocalized(entry.description, lang);
  const finalImage = image ?? ogImageForRoute(canonicalRoute);

  const buildUrl = (l: SupportedLang) => {
    const localized = withLangPrefix(canonicalRoute, l);
    return `${SITE_URL}${localized === "/" ? "" : localized}`;
  };
  const canonical = buildUrl(urlLang);
  const ogLocale =
    lang === "ar" ? "ar_SA" : lang === "hi" ? "hi_IN" : lang === "ru" ? "ru_RU" : "en_US";
  const shouldNoIndex = noIndex ?? entry.noIndex ?? false;

  // Per-page WebPage + BreadcrumbList (+ Service for features) JSON-LD.
  // Same builder is used by scripts/prerender.ts so server-rendered HTML
  // and runtime hydrate produce identical structured data.
  const pageJsonLd = buildPageJsonLd({
    canonicalRoute,
    lang,
    title: finalTitle,
    description: finalDescription,
    image: finalImage,
    canonical,
  });

  return (
    <Helmet>
      <html lang={lang} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {entry.keywords && <meta name="keywords" content={entry.keywords} />}
      <meta
        name="robots"
        content={
          shouldNoIndex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />
      <link rel="canonical" href={canonical} />

      {/* Hreflang alternates */}
      {SUPPORTED_LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={buildUrl(l)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildUrl("en")} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Per-page structured data — WebPage + BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify(pageJsonLd)}
      </script>
    </Helmet>
  );
};

export default SEO;